<template>
  <section class="ip-block__content"
           v-if="!isLoading">
    <header class="content__header">
      <h2 v-html="$t('userInfo.lnb.ipBlocking')"></h2>
    </header>
    <div class="content__body" ref="scrollContent">
      <template v-if="domains.list && domains.list.length">
        <content-box class="content__target-domain" :width="'800px'">
          <p class="title" v-html="$t('userInfo.ipBlocking.selectDomain.title')"></p>
          <div class="domain--wrap">
            <label class="label" for="targetDomain" v-html="$t('common.domain')"></label>
            <UiSelectbox id="targetDomain"
                        @input="onSelectedDomain"
                        :useRawText="true"
                        :value="domains.selectedValue"
                        :items="domains.list" />
          </div>
        </content-box>
        <content-box class="content__target-ip" :width="'800px'">
          <div class="content__top">
            <p class="title" v-html="$t('userInfo.ipBlocking.enterIp.title')"></p>
            <div class="input--wrap">
              <p class="desc" v-html="$t('userInfo.ipBlocking.enterIp.desc')"></p>
              <div class="ip-info-box">
                <div class="ip-name">
                  <label class="label" for="ipName" v-html="$t('common.ipName')"></label>
                  <input type="text"
                        id="ipName"
                        :placeholder="$t('common.placeHolder.enterIpName')"
                        v-model.trim="model.name"
                        @focus="modelError.name = ''"
                        :class="{error: modelError.name}">
                  <i v-if="model.name.length" class="icon icon--close14" @click="model.name = ''"></i>
                  <strong v-if="modelError.name" class="validation-text"
                          v-html="$t(`common.validationErrorText.ipBlock.${modelError.name}`)"></strong>
                </div>
                <div class="ip-address">
                  <label class="label" for="ipAddress" v-html="$t('common.ipAddress')"></label>
                  <input type="text" id="ipAddress"
                        :placeholder="$t('common.placeHolder.ipAddress')"
                        v-model.trim="model.ip"
                        @focus="modelError.ip = ''"
                        :class="{error: modelError.ip}">
                  <i v-if="model.ip.length" class="icon icon--close14" @click="model.ip = ''"></i>
                  <strong v-if="modelError.ip" class="validation-text"
                          v-html="$t(`common.validationErrorText.ipBlock.${modelError.ip}`)"></strong>
                </div>
              </div>
            </div>
            <div class="button--wrap">
              <ui-button class="btn__add-my-ip"
                        :iconName="'icon--plus-black'"
                        :noBorder="true"
                        @click="onAddMyIp">{{ $t('userInfo.ipBlocking.addMyIp') }}</ui-button>
              <ui-button class="btn__block"
                        :primary2="true"
                        @click="onAddIpListItem">{{ $t('userInfo.ipBlocking.block') }}</ui-button>
            </div>
          </div>
          <div class="content__bottom">
            <table>
              <colgroup>
                <col width="280">
                <col width="282">
                <col width="188">
              </colgroup>
              <thead>
              <tr>
                <th>{{ $t('common.ipName') }}</th>
                <th>{{ $t('common.ipAddress') }}</th>
                <th></th>
              </tr>
              </thead>
              <tbody v-if="ipList">
              <ip-block-item v-for="(item, index) in ipList"
                            :key="index"
                            :item="item"
                            :ipList="ipList"
                            @removeListItem="onRemoveIpListItem"
                            @editListItem="onEditListItem"/>
              </tbody>
              <tbody v-else>
              <tr class="no-ip-list">
                <td rowspan="2" colspan="3" v-html="$t('userInfo.ipBlocking.noRegisteredIp')"></td>
              </tr>
              <tr>
              </tr>
              </tbody>
            </table>
          </div>
        </content-box>
        <back-to-top :refName="'scrollContent'" />
      </template>
      <div v-else class="no-register-page">
        <i class="icon icon--report-empty"></i>
        <p class="desc" v-html="$t('userInfo.ipBlocking.empty')"></p>
      </div>
    </div>
  </section>
</template>

<script>
import { EventBus } from '@/libs/event-bus'
import { getDomains } from '@/api/domain'
import { myIp, getIpList, insertIp, removeIp, updateIp } from '@/api/ipblock'
import { isValidIpAddress } from '@/libs/validation-helper'
import ContentBox from '@/components/ui/ContentBox'
import UiSelectbox from '@/components/ui/UiSelectbox'
import BackToTop from '@/components/ui/BackToTop'
import UiButton from '@/components/ui/UiButton'
import ipBlockItem from '@/components/user-info/ipblock/IpBlockItem'
import AppDialog from '@/components/ui/AppDialog'
import { create } from 'vue-modal-dialogs'
import { getDomainForView } from '@/libs/utils'

const openDialog = create(AppDialog, 'dialogType')

export default {
  components: {
    ContentBox,
    UiSelectbox,
    BackToTop,
    UiButton,
    ipBlockItem
  },
  data () {
    return {
      isLoading: true,
      model: {
        name: '',
        ip: ''
      },
      modelError: {
        name: '',
        ip: ''
      },
      domains: {
        list: [],
        sid: [],
        selectedValue: '',
        currentDomainSid: ''
      },
      ipList: null
    }
  },
  methods: {
    load () {
      getDomains().then(items => {
        this.domains.list = items.filter(item => item.matchType === 'simple' && item.shared !== 'shared').map(item => getDomainForView(item.domain))
        this.domains.sid = items.filter(item => item.matchType === 'simple' && item.shared !== 'shared').map(item => item.orgSid)
        this.domains.selectedValue = this.domains.list[0]
        this.onSelectedDomain(this.domains.selectedValue)
      }).finally(() => {
        EventBus.$emit('onLoading', false)
        this.isLoading = false
      })
    },
    onSelectedDomain (value) {
      this.modelError.name = ''
      this.modelError.ip = ''
      this.domains.selectedValue = value
      this.domains.list.forEach((item, index) => {
        if (item === value) {
          this.domains.currentDomainSid = this.domains.sid[index]
        }
      })
      this.getDomainIpList()
    },
    getDomainIpList () {
      getIpList(this.domains.currentDomainSid).then(response => {
        let ipList = []
        response['ip'].forEach((item) => {
          item['edit'] = false
          ipList.push(item)
        })
        this.ipList = ipList
      }).catch(() => {
        this.ipList = null
      })
    },
    onAddMyIp () {
      this.model.name = this.$t('userInfo.ipBlocking.myIp')
      this.modelError.name = ''
      myIp().then(response => {
        this.model.ip = response
        this.modelError.ip = ''
      })
    },
    onAddIpListItem () {
      if (!this.model.name) {
        this.modelError.name = 'requiredIpName'
      }
      if (!this.model.ip) {
        this.modelError.ip = 'requiredIpAddress'
      }

      if (this.ipList) {
        this.ipList.forEach(item => {
          if (this.model.name && item.name === this.model.name) {
            this.modelError.name = 'existName'
          }
          if (this.model.ip && item.ip === this.model.ip) {
            this.modelError.ip = 'existIp'
          }
        })
      }

      if (!this.modelError.ip && !isValidIpAddress(this.model.ip)) {
        this.modelError.ip = 'diffFormat'
      }

      if (this.modelError.name || this.modelError.ip) return false

      let param = {
        ...this.model,
        sid: this.domains.currentDomainSid,
        domain: this.domains.selectedValue
      }
      insertIp(param).then(() => {
        this.model.name = ''
        this.model.ip = ''
        this.getDomainIpList()
      })
    },
    onRemoveIpListItem (item) {
      openDialog('userInfo.ipBlockItemDelete').then(result => {
        if (result) {
          let id = { id: item.id }
          removeIp(id).then(() => {
            this.getDomainIpList()
          })
        }
      })
    },
    onEditListItem (data) {
      let param = {
        id: data.item.id,
        name: data.model.name,
        ip: data.model.ip
      }
      openDialog('userInfo.ipBlockConfirmEdit').then(result => {
        if (result) {
          updateIp(param).then(() => {
            data.item.edit = false
            this.getDomainIpList()
          })
        }
      })
    }
  },
  created () {
    this.load()
  }
}
</script>

<style lang="scss" scoped="scoped">
.ip-block__content {
  box-sizing: border-box;
  padding-top: 40px;

  .content__header {
    font-size: 26px;
    font-weight: 600;
    color: #444444;
    margin-left: 30px;
    margin-right: 30px;
    padding-bottom: 22px;
    border-bottom: 1px solid #d7d7d7;

    h2 {
      padding-left: 10px;
    }
  }

  .content__body {
    position: relative;
    height: calc(100vh - 166px);
    padding-top: 30px;

    .no-register-page {
      @include positionXYcenter;
      padding: 0 0 102px;
      text-align: center;
      .desc {
        margin-top: 16px;
        color: #777;
        font-size: 13px;
        font-weight: 500;
        line-height: 1;
      }
    }

    .content__target-domain,
    .content__target-ip {
      margin: 0 auto;
      box-sizing: border-box;
    }

    .title {
      font-size: 15px;
      font-weight: 600;
      line-height: 1.47;
      color: #2f2f2f;
    }

    .label {
      font-size: 13px;
      font-weight: 600;
      color: #767676;
      display: block;
      text-align: left;
      padding-left: 6px;
    }

    .content__target-domain {
      @include flexXbetween;
      align-items: center;
      padding: 50px 40px;

      .title {
        max-width: 160px;
      }

      .domain--wrap {
        width: 530px;
        /deep/ .ui_select {
          margin-top: 5px;
          width: 100%;
        }
      }
    }

    .content__target-ip {
      margin-top: 30px;

      & > [class*=content__] {
        box-sizing: border-box;
      }

      input[type=text] {
        width: 100%;
        height: 38px;
        border-radius: 6px;
        border: solid 1px #bbbbbb;
        background-color: #ffffff;
        padding-left: 12px;
        padding-right: 40px;
        box-sizing: border-box;
        font-size: 14px;
        margin-top: 5px;

        &:focus {
          border-color: #289eef;
        }

        &.error {
          border-color: $errorColor;
        }
      }

      .validation-text {
        @include validErrorText;
        position: absolute;
        left: 6px;
        bottom: -17px;
      }

      .icon--close14 {
        position: absolute;
        top: calc(50% + 9px);
        right: 2px;
        padding: 9px 12px;
        background-position: center;
        background-color: transparent;
        transform: translateY(-50%);
      }

      .content__top {
        padding: 48px 40px 30px;

        .input--wrap {
          margin-top: 40px;
          @include flexXbetween;
          align-items: center;

          .desc {
            max-width: 160px;
            font-size: 12px;
            line-height: 1.5;
            color: $brandColor;

          }

          .ip-info-box {
            display: flex;
          }

          .ip-name {
            position: relative;
            display: inline-block;
            width: 335px;
          }

          .ip-address {
            position: relative;
            display: inline-block;
            width: 190px;
            margin-left: 5px;
          }
        }
        .button--wrap {
          @include flexYcenter;
          justify-content: flex-end;
          margin-top: 40px;

          .btn__add-my-ip {
            font-size: 14px;
            font-weight: 600;
            line-height: 1;
            color: #444444;
            margin-right: 15px;

            .icon {
              vertical-align: text-top;
              margin-right: 8px;
            }
          }
        }
      }
      .content__bottom {
        border-top: 1px solid #d8d8d8;
        padding: 20px 25px 30px;

        table {
          font-size: 13px;
          line-height: 1.23;
          thead tr {
            height: 50px;
            background-color: #555555;

            th {
              position: relative;
              text-align: center;
              color: #ffffff;

              &:nth-of-type(1):before {
                content: "";
                display: inline-block;
                width: 15px;
                height: 50px;
                position: absolute;
                top: 0;
                left: -5px;
                background-color: #555;
                border-radius: 5px;
              }

              &:nth-last-of-type(1):after {
                content: "";
                display: inline-block;
                width: 10px;
                height: 50px;
                position: absolute;
                top: 0;
                right: -5px;
                background-color: #555;
                border-radius: 5px;
              }
            }
          }
          tbody tr {
            height: 50px;
            border-bottom: 1px solid #ebebeb;
            box-sizing: border-box;

            &.no-ip-list {
              text-align: center;
            }

            td {
              font-size: 13px;
              line-height: 1.23;
              color: #2f2f2f;

              .input--wrap {
                position: relative;
                display: inline-block;
                input[type=text] {
                  width: 157px;
                  margin-top: 0;
                }
                .icon--close14 {
                  top: 50%;
                }
              }

              &.data__ip-name {
                padding-left: 10px;
              }

              &.data__ip-address {
                text-align: center;
              }

              &.data__function {
                text-align: right;

                /deep/ .ui-btn {
                  box-sizing: border-box;
                  min-width: 0;
                  width: 34px;
                  height: 34px;

                  &:nth-last-of-type(1) {
                    margin-left: 5px;
                  }

                  .icon {
                    @include positionXYcenter;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
